import React, { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { useForm, useFormState } from "react-final-form";
import classNames from "classnames";
import { uniqBy } from "lodash";
import { isNotEmpty } from "utils/validate";
import SearchSelectInput from "rs-components/Input/components/SearchSelectInput";
import Input from "rs-components/Input";
import TimingFields from "rs-components/TimingFields";
import { FormField } from "../../index";
import SelectInput from "rs-components/Input/components/SelectInput";
import SelectItemRenderer from "rs-components/Input/components/SelectInput/components/SelectItemRenderer";
import { parameterTypesSelector, processVariablesSelector } from "store/global/global.selectors";
import { ProcessVariableType, } from "store/rs-workspace/rs-workspace.types";
import { workspaceRecipesSelector } from "store/rs-workspace/selectors/rs-workspace.selectors";
import { getAllRecipeTimestamps } from "store/rs-workspace/utils/insights.utils";
import { isEditableSelector } from "store/knowledge-base-item/knowledge-base-item.selectors";
import { CustomVariableRelativeValueVariableType } from "store/recipe/recipe.types";
import { getAvailableProcessVariableCategories, getProcessVariablesToSelect, getProcessVariablesUsedUnits, getProcessVariableUnitsOptions, } from "store/rs-workspace/utils/results.utils";
import { GET_ALL_CUSTOM_VARIABLES, getRecipesCustomVariables } from "store/rs-workspace/utils/scenario.utils";
import { parseComplexUnitToHtml } from "store/recipe/custom-variables.utils";
import * as styles from "../../styles.module.scss";
const ProcessVariableFormField = ({ processVariableRecipeIds, customVariablesWithRecipeName, selectedParameters, restrictions = {}, }) => {
    const form = useForm();
    const formState = useFormState();
    const processVariables = useSelector(processVariablesSelector);
    const editable = useSelector(isEditableSelector);
    const parameterTypes = useSelector(parameterTypesSelector);
    const workspaceRecipes = useSelector(workspaceRecipesSelector);
    const recipes = useSelector(workspaceRecipesSelector);
    const { values } = useMemo(() => ({ ...formState }), [formState]);
    const isCustomVariable = useMemo(() => values?.parameter?.type === ProcessVariableType.Custom, [values?.parameter?.type]);
    const selectedCustomVariable = useMemo(() => (isCustomVariable ? values?.parameter : null), [isCustomVariable, values?.parameter]);
    const { variableRestrictions, unitsRestrictions, cellModelRestrictions } = useMemo(() => ({
        variableRestrictions: isCustomVariable
            ? restrictions?.Custom?.CustomVariableId
            : restrictions?.CellModel?.VariableType,
        unitsRestrictions: values?.parameter?.type === ProcessVariableType.CellModel ? restrictions?.CellModel?.Units : {},
        cellModelRestrictions: values?.parameter?.type === ProcessVariableType.CellModel ? restrictions?.CellModel : {}, //TODO: expand this condition when type Recipe is available
    }), [
        isCustomVariable,
        values?.parameter?.type,
        restrictions?.CellModel,
        restrictions?.Custom?.CustomVariableId,
    ]);
    const customVariables = useMemo(() => getRecipesCustomVariables(recipes, processVariableRecipeIds ?? GET_ALL_CUSTOM_VARIABLES, (variable) => {
        if (isNotEmpty(selectedCustomVariable)) {
            return (Boolean(variable.recordInData) &&
                (variable.formula?.complete || variable.id === selectedCustomVariable?.customVariableId));
        }
        return Boolean(variable.recordInData) && !!variable.formula?.complete;
    }), [recipes, processVariableRecipeIds, selectedCustomVariable]);
    const parametersToSelect = useMemo(() => getProcessVariablesToSelect(getAvailableProcessVariableCategories(processVariables), customVariables)?.map((item) => {
        const { id, recipeId, formula } = { ...item.value };
        const recipeName = customVariablesWithRecipeName
            ? recipes.find(({ id }) => id === recipeId)?.name
            : undefined;
        return {
            ...item,
            label: `${item.label}${recipeName ? ` (${recipeName})` : ""}`,
            disabled: isNotEmpty(selectedCustomVariable) &&
                id === selectedCustomVariable?.customVariableId &&
                recipeId === selectedCustomVariable?.recipeId &&
                formula?.complete,
        };
    }), [processVariables, customVariables, recipes, customVariablesWithRecipeName, selectedCustomVariable]);
    const timestampOptions = useMemo(() => uniqBy(getAllRecipeTimestamps(workspaceRecipes), "label"), [workspaceRecipes]);
    const handleChangeProcessVariable = useCallback((value) => {
        if (value.type === CustomVariableRelativeValueVariableType.CustomVariable) {
            const customVariableValue = {
                [FormField.TYPE]: ProcessVariableType.Custom,
                [FormField.CUSTOM_VARIABLE_ID]: value.key,
                [FormField.RECIPE_ID]: value.recipeId,
            };
            form.change(FormField.PARAMETER, customVariableValue);
        }
        else {
            const usedUnits = getProcessVariablesUsedUnits(parameterTypes, selectedParameters, value.key, value.units);
            const availableUnits = getProcessVariableUnitsOptions(parameterTypes, value.units);
            const unitsToSelect = usedUnits?.includes(value.units) ? availableUnits[0]?.value : value.units;
            const cellModelVariableValue = {
                [FormField.TYPE]: ProcessVariableType.CellModel,
                [FormField.VARIABLE_TYPE]: value.key,
                [FormField.UNITS]: unitsToSelect,
            };
            form.change(FormField.PARAMETER, cellModelVariableValue);
        }
    }, [form, parameterTypes, selectedParameters]);
    const selectedVariable = useMemo(() => {
        switch (values.parameter?.type) {
            case ProcessVariableType.CellModel: {
                const { variableType } = { ...values?.parameter };
                if (isNotEmpty(variableType)) {
                    return parametersToSelect.find(({ value }) => value.type === CustomVariableRelativeValueVariableType.ProcessVariable &&
                        value.key === variableType);
                }
                return;
            }
            case ProcessVariableType.Custom: {
                const { customVariableId, recipeId } = { ...values?.parameter };
                if (isNotEmpty(customVariableId)) {
                    const selectedVariable = getRecipesCustomVariables(recipes, [recipeId], (variable) => variable.id === customVariableId)[0];
                    const recipeName = customVariablesWithRecipeName
                        ? recipes.find(({ id }) => id === recipeId)?.name
                        : undefined;
                    const label = `${selectedVariable.name}${recipeName ? ` (${recipeName})` : ""}`;
                    return selectedVariable ? { value: selectedVariable, label } : undefined;
                }
                return;
            }
        }
    }, [customVariablesWithRecipeName, parametersToSelect, recipes, values.parameter]);
    const { variableUnits, variableUnitsOptions } = useMemo(() => {
        if (values.parameter?.type === ProcessVariableType.CellModel) {
            return {
                variableUnits: values?.parameter?.units,
                variableUnitsOptions: getProcessVariableUnitsOptions(parameterTypes, values?.parameter?.units),
            };
        }
        else if (values.parameter?.type === ProcessVariableType.Custom) {
            const variable = parametersToSelect.find(({ value }) => value.type === CustomVariableRelativeValueVariableType.CustomVariable &&
                value.key === values.parameter.customVariableId)?.value;
            const variableUnits = parseComplexUnitToHtml(variable?.formula?.configuredUnits?.complexValue)?.html;
            return {
                variableUnits,
                variableUnitsOptions: [{ value: variableUnits, label: variableUnits }],
            };
        }
        else {
            return {
                variableUnits: undefined,
                variableUnitsOptions: [],
            };
        }
    }, [values.parameter, parameterTypes, parametersToSelect]);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: styles.field },
            React.createElement(Input, { readonly: !editable || (parametersToSelect.length === 0 && !isNotEmpty(values?.parameter?.type)), component: SearchSelectInput, name: isCustomVariable
                    ? `${FormField.PARAMETER}.${FormField.CUSTOM_VARIABLE_ID}`
                    : `${FormField.PARAMETER}.${FormField.VARIABLE_TYPE}`, disableFormChange: true, onChange: (value) => handleChangeProcessVariable(value), label: "Variable", placeholder: !parametersToSelect.length ? "No process variables to add." : "Select", undefinedReadonlyPlaceholder: !editable, items: parametersToSelect, value: selectedVariable, classes: {
                    component: classNames({ [styles["field-noSources"]]: !parametersToSelect.length }),
                    label: styles["field-label"],
                }, restrictions: variableRestrictions })),
        !!selectedVariable?.value && (React.createElement("div", { className: styles.field },
            React.createElement(Input, { readonly: !editable, component: SelectInput, name: `${FormField.PARAMETER}.${FormField.UNITS}`, label: "Unit", items: variableUnitsOptions, value: variableUnits, disabled: isCustomVariable, valueFormatter: ({ value }) => value, classes: { component: styles.selectWrapper, label: styles["field-label"] }, itemRenderer: SelectItemRenderer, restrictions: unitsRestrictions }))),
        (isNotEmpty(variableUnits) || (isCustomVariable && !!selectedVariable?.value)) && (React.createElement(TimingFields, { name: `${FormField.PARAMETER}.${FormField.CUT}`, namePrefix: `${FormField.PARAMETER}.`, values: values.parameter?.cut ?? undefined, additionalTimestampOptions: timestampOptions, restrictions: isCustomVariable ? restrictions?.Custom : cellModelRestrictions, recipeIds: processVariableRecipeIds, excludedVariables: isCustomVariable
                ? [
                    {
                        value: values.parameter?.customVariableId,
                        label: "",
                    },
                ]
                : [] }))));
};
export default ProcessVariableFormField;
